// OpenMeta — English dictionary (native marketing tone)

export const en = {
  common: {
    email_placeholder: 'you@email.com',
    already_in: "You're already on the list! ✨",
    lang_short_fr: 'FR',
    lang_short_en: 'EN',
    switch_lang: 'Switch language',
    switch_theme_dark: 'Switch to dark mode',
    switch_theme_light: 'Switch to light mode',
    close: 'Close',
    open_menu: 'Open menu',
  },
  errors: {
    generic: 'Something went wrong. Please try again.',
    rate_limit_generic: 'Too many requests. Please come back in a moment.',
    rate_limit_generator:
      "You've reached your generation limit — please come back a bit later.",
    rate_limit_login: 'Too many login attempts. Please try again in a few minutes.',
    llm_unavailable: 'The generator is momentarily unavailable — please try again shortly.',
    email_required: 'Please enter your email.',
    email_invalid: "This email address doesn't look valid.",
    email_too_long: 'This email address is too long.',
    email_duplicate: "You're already on the list!",
    generate_generic: 'Generation failed. Please try again.',
    concept_not_found: "This concept no longer exists. Generate a new one.",
    unlock_generic: "We couldn't unlock your concept right now.",
    unlock_rate_limit: 'Too many attempts — please come back in a moment.',
    contact_generic: "We couldn't send your message right now. Please try again shortly.",
    contact_invalid_fields: 'Some of the information looks invalid.',
    checkout_generic: 'Payments are momentarily unavailable, please try again shortly.',
    checkout_invalid: 'This plan is not valid. Reload the page and try again.',
    prototype_generic: 'Cannot build the prototype right now.',
    prototype_locked: 'This concept is not unlocked yet.',
    prototype_not_found: "This concept doesn't exist anymore.",
    prototype_rate_limit: 'Too many prototypes built, please come back in a moment.',
    prototype_unavailable: 'Building the prototype failed, please try again shortly.',
    auth_bad_credentials: 'Email or password is incorrect.',
    auth_email_taken: 'An account already exists with this email.',
    auth_invalid_input: 'Some of the information looks invalid.',
    auth_rate_limit: 'Too many attempts, please come back in a moment.',
    auth_generic: 'Something went wrong. Please try again shortly.',
    auth_terms_required: 'You must accept the Terms of Use and the Privacy Policy to create your account.',
  },
  navbar: {
    home: 'Home',
    applications: 'Applications',
    universe: 'Verticals',
    universe_short: 'Verticals',
    generator: 'Generator',
    pricing: 'Pricing',
    contact: 'Contact',
    cta_waitlist: 'Join the waitlist',
  },
  waitlist: {
    submit: 'Join the waitlist',
    submitting: 'Sending…',
    submitted: 'Thanks! ✨',
    success: 'Welcome to the OpenMeta waitlist! ✨',
    already_in: "You're already on the list! ✨",
    no_spam: 'No credit card. No spam.',
    no_spam_short: 'Free — just your email, no spam.',
    counter_prefix: 'Already',
    counter_suffix_single: 'creator on board.',
    counter_suffix_plural: 'creators on board.',
    counter_chip_single: 'creator on the waitlist',
    counter_chip_plural: 'creators on the waitlist',
  },
  footer: {
    tagline:
      'The No-Code + AI platform that hands the keys back to domain experts. Five verticals, infinite possibilities.',
    section_universe: 'Verticals',
    section_product: 'Product',
    section_info: 'Info',
    product_applications: 'Our Applications',
    product_generator: 'Generator',
    product_pricing: 'Pricing',
    product_contact: 'Contact',
    info_legal: 'Legal notice',
    info_privacy: 'Privacy',
    info_terms: 'Terms of Use',
    copyright: '© {year} OpenMeta.tech — All rights reserved.',
    slogan: 'No Code. No Limits. Just AI.',
  },
  hero: {
    eyebrow: 'No-Code + AI Platform — 5 domain verticals',
    title_l1: 'No Code.',
    title_l2: 'No Limits.',
    title_l3: 'Just AI.',
    subtitle:
      "The No-Code + AI platform that gives domain experts the power to build — doctors, teachers, artists, linguists, content creators. Describe your idea, the AI assembles the blocks.",
    universes_label: 'The 5 OpenMeta verticals',
    illustration_alt:
      'Composition of a no-code app assembled from colorful translucent 3D blocks',
  },
  home: {
    universes: {
      eyebrow: '5 verticals, 5 domain languages',
      title_l1: 'A modular ecosystem',
      title_l2: 'for every domain.',
      subtitle:
        "Each vertical bundles its own no-code blocks, specialized AI models and key screens. You pick one, describe your project — the AI takes it from there.",
    },
    applications_teaser: {
      eyebrow: 'Our Applications',
      title_l1: 'Already shipping.',
      title_l2: 'Built with OpenMeta.',
      see_all: 'See all our applications',
    },
    how: {
      eyebrow: 'How it works',
      title_l1: 'Three steps.',
      title_l2: 'Zero lines of code.',
      subtitle:
        "OpenMeta never asks you to think 'app' or 'product'. You speak business impact, the AI does the translation.",
      steps: [
        {
          title: 'Describe your idea',
          text: "In a few sentences, explain the business problem you want to solve. No technical talk — just how you'd tell a colleague.",
        },
        {
          title: 'AI assembles your blocks',
          text: 'OpenMeta picks the right no-code blocks, sketches the key screens and composes a coherent, ready-to-customize app.',
        },
        {
          title: 'Publish your application',
          text: 'Tune the copy, visuals and your vertical color — then publish in one click. Your users are already there.',
        },
      ],
    },
    testimonials: {
      eyebrow: 'Already building with OpenMeta',
      title_l1: 'Domain experts',
      title_l2: 'take the lead.',
      quotes: [
        {
          name: 'Dr. Amélie Fournier',
          role: 'General practitioner, Lyon',
          quote:
            "I shipped a voice follow-up journal for my elderly patients in one evening. They use it daily — and I finally get reliable data between appointments.",
        },
        {
          name: 'Claire Vasseur',
          role: 'Middle-school teacher, Nantes',
          quote:
            "My students are learning with a French AI coach I dreamed up on a Sunday night. Without OpenMeta, it would have taken six months — and I'd still be writing a spec.",
        },
        {
          name: 'Malik Sadaoui',
          role: 'Visual artist, Marseille',
          quote:
            "My exhibition now has a digital companion that tells the story of each piece. Visitors stay three times longer. And I never touched a single line of code.",
        },
      ],
    },
    waitlist_cta: {
      eyebrow: 'Early access',
      title_l1: "Don't wait",
      title_l2: 'for the next wave.',
      subtitle:
        "Join the builders who'll get OpenMeta first. One email — that's all it takes.",
    },
  },
  verticals: {
    sante: {
      label: 'Health & Wellness',
      short: 'Health',
      tagline: 'Care, prevention and well-being — amplified by AI.',
      description:
        "Tailored apps for caregivers, patients and wellness practitioners: follow-ups, prevention, daily support.",
      promise: 'Ship your health app in 30 minutes.',
      intro:
        "OpenMeta bundles the blocks and AI that health professionals and wellness pros actually need — with no engineering team involved.",
      use_cases: [
        { title: 'Patient follow-up assistant', text: 'Log vitals, symptoms and mood by voice, then generate a readable summary for the next consultation.' },
        { title: 'Well-being AI journal', text: 'A daily companion: mood, sleep, guided breathwork, personalized encouragement.' },
        { title: 'Smart appointment booking', text: 'A form that asks the right questions, routes to the right practitioner and auto-confirms the slot.' },
        { title: 'Post-op follow-up protocol', text: 'Deliver the right recommendations at the right time, with gentle nudges and alerts on weak signals.' },
      ],
    },
    culture: {
      label: 'Culture & Art',
      short: 'Culture',
      tagline: 'Bring heritage to life, reveal artists, spark emotion.',
      description:
        "Immersive experiences for museums, galleries, artists and festivals: interactive journeys, smart catalogs, engaged audiences.",
      promise: 'Give your works and venues a voice in 30 minutes.',
      intro:
        "OpenMeta lets artists, galleries and cultural institutions craft immersive digital experiences — without a single line of code.",
      use_cases: [
        { title: 'Exhibition companion', text: 'A smart tour that tells the story of each piece, adapts to the visitor age and offers playful moments.' },
        { title: 'Interactive catalog', text: 'Digitize your collection, weave connections between works and let audiences explore your heritage.' },
        { title: 'Inspired ticketing', text: 'An elegant booking funnel with personalized recommendations and pre-visit reminders.' },
        { title: 'Living artist portfolio', text: 'Showcase your journey and work, then let AI surface the best pieces for every collector profile.' },
      ],
    },
    formation: {
      label: 'Learning & Education',
      short: 'Learning',
      tagline: 'Teach, upskill, certify — with zero friction.',
      description:
        "Personalized learning spaces for trainers, schools and companies: pedagogical journeys, AI coaching, certifications.",
      promise: 'Launch your learning journey in 30 minutes.',
      intro:
        "OpenMeta gives trainers, schools and HR leaders everything they need to design engaging, personalized, effective learning journeys.",
      use_cases: [
        { title: 'Domain micro-courses', text: 'Short video and text modules with AI quizzes to upskill teams at their own pace on key competencies.' },
        { title: 'AI skills coach', text: 'A virtual mentor that answers learners, corrects exercises and flags topics that need more work.' },
        { title: 'Certification track', text: 'Track each learner, deliver digital certificates and centralize proof of competencies.' },
        { title: 'New hire onboarding', text: 'An onboarding companion that surfaces the right resources day by day and answers repeat questions.' },
      ],
    },
    langues: {
      label: 'Language Learning',
      short: 'Languages',
      tagline: 'Learn a language through conversation, immersion and play.',
      description:
        'Conversational experiences for learners, language schools and polyglots: immersive dialogues, AI pronunciation, playful progression.',
      promise: 'Build your live language coach in 20 minutes.',
      intro:
        "OpenMeta turns language learning into conversations, games and immersion — far beyond a vocabulary app.",
      use_cases: [
        { title: 'Conversational coach', text: 'A tireless conversation partner that adapts vocabulary and gently corrects pronunciation.' },
        { title: 'Business immersion', text: 'Real-world scenarios: negotiate in English, pitch a project, run an interview.' },
        { title: 'Travel companion', text: 'The 200 must-know phrases, live help and a library of cultural contexts for every destination.' },
        { title: 'Daily language challenge', text: 'A playful, rewarding daily mini-game that anchors vocabulary without effort.' },
      ],
    },
    social: {
      label: 'AI Social Media',
      short: 'Social',
      tagline: 'Create, publish, engage — at the pace of modern creators.',
      description:
        "Tools for creators, community managers and brands: content generation, scheduling, moderation, impact measurement.",
      promise: 'Assemble your content studio in 20 minutes.',
      intro:
        "OpenMeta equips creators and indie brands to publish, engage and measure — without going through an agency.",
      use_cases: [
        { title: 'AI content studio', text: 'Generate captions, hashtags and variants, schedule multi-platform posts and keep your brand voice.' },
        { title: 'Assisted community manager', text: 'Reply faster to comments and DMs, with suggestions calibrated to your voice.' },
        { title: 'Audience analytics', text: 'Understand who follows you, what drives engagement and where to focus next.' },
        { title: 'Enriched newsletter', text: 'Turn your top posts into an automated newsletter to reach your community beyond the platforms.' },
      ],
    },
  },
  univers_page: {
    back_home: '← Back to home',
    universe_badge_prefix: 'Vertical',
    cta_generator: 'Try the generator',
    cta_waitlist: 'Join the waitlist',
    section_use_cases_eyebrow: 'Use cases',
    section_use_cases_title: 'What you can build.',
    section_showcases_eyebrow: 'Concepts created with OpenMeta',
    section_showcases_title: "They've already imagined their app.",
    section_showcases_subtitle:
      "These concepts were generated in seconds by users like you, from a single description.",
    showcases_empty: 'The showcases for this vertical are coming very soon.',
    cta_generate_mine: 'Generate mine',
    waitlist_eyebrow: 'Early access',
    waitlist_title_l1: 'Be among the first to build in',
  },
  applications_page: {
    eyebrow: 'Our Applications',
    title_l1: 'Real applications,',
    title_l2: 'for real professions.',
    subtitle:
      "Here are a few of the applications already built inside the OpenMeta ecosystem — designed by and for domain experts, running on their own domains.",
    cta_eyebrow: 'The next one is yours.',
    cta_title_l1: 'Ready to imagine',
    cta_title_l2: 'your application?',
    cta_subtitle: "Join the waitlist — you'll be among the first to build with OpenMeta.",
    visit_app: 'Visit the application',
  },
  applications_data: {
    'aria-radiology': {
      tagline: 'AI-assisted radiology analysis.',
      description:
        "A practitioner space where AI helps interpret medical imaging in support of the radiologist. An ally for more confidence and speed on complex cases.",
    },
    medurgence: {
      tagline: 'Medical AI assistant for emergency care.',
      description:
        "Step-by-step protocols for cardiac arrest, stroke, shock and status, weight-based dose calculator, interaction checks and an AI decision-support chat, backed by Urg'Ara guidelines.",
    },
    medlib: {
      tagline: 'Not sure about a health issue? Know what to do and who to call.',
      description:
        "A consumer health triage assistant: a few guided questions point you to the right level of care — emergency services, ER, same-day GP, your usual doctor or your pharmacist. Twelve free first-aid cards, available offline. No diagnosis, no dosing — does not replace medical advice.",
    },
    atlantys: {
      tagline: 'Social network of AI agents.',
      description:
        "A platform where hundreds of AI agents — ministers, thematic advisors — publish, debate and propose public policies on major contemporary issues, in partnership with Sciences Po & AI.",
    },
    'urgence-pratique': {
      tagline: 'AI clinical copilot for ER and prehospital teams.',
      description:
        "An intelligent assistant for healthcare professionals only: structured, sourced answers aligned with SFMU, SFAR, SRLF and HAS guidelines, Prescrire AI, real-time voice assistant, decision-support protocols, clinical scoring and dose calculators, drug sheets and anonymized patient tracking.",
    },
    medsynapse: {
      tagline: 'The medical social network powered by 850 expert AI agents.',
      description:
        "51 medical communities, open clinical discussions and the collegial opinion of a 250-specialist staff — a free platform to share, learn and compare practices.",
    },
    'lexia-school': {
      tagline: 'Learn languages with your voice AI coach.',
      description:
        "A 9-step method — word, meaning, pronunciation, mental image, sentence, context, listening, production, review — orchestrated by Lexia, a voice coach who listens, corrects and gets you speaking from minute one.",
    },
  },
  pricing: {
    eyebrow: 'Pricing',
    title_l1: 'Fair pricing,',
    title_l2: 'for every stage.',
    subtitle:
      "Pick Basic to launch your first app, Pro to orchestrate up to 10 apps with no AI limits, or Enterprise for hands-on support.",
    toggle_monthly: 'Monthly',
    toggle_annual: 'Annual',
    discount: '-20%',
    popular: 'Most popular',
    price_month_suffix: '/month',
    price_year_suffix: '/year',
    price_on_quote: 'On request',
    faq_eyebrow: 'FAQ',
    faq_title_l1: 'Your questions,',
    faq_title_l2: 'our answers.',
    waitlist_eyebrow: 'Early access',
    waitlist_title: 'Lock in your launch pricing.',
    waitlist_subtitle:
      'Waitlist members will receive an exclusive welcome discount at launch.',
    checkout_loading: 'Redirecting to checkout…',
    checkout_error: 'Payments are momentarily unavailable, please try again shortly.',
    checkout_cancel_toast: 'Payment cancelled — you can resume anytime.',
    checkout_cancel_banner_title: 'Payment cancelled.',
    checkout_cancel_banner_subtitle: 'No charge — pick it back up whenever.',
    checkout_cancel_banner_close: 'Close',
    tiers: [
      {
        key: 'basique',
        name: 'Basic',
        tag: '€29/month',
        tagAnnual: '€279/year',
        description: 'To confidently launch your first AI application.',
        features: [
          '1 published application',
          'AI concept generator included',
          'Essential no-code blocks from all 5 verticals',
          'openmeta.tech subdomain',
          'Email support',
        ],
        cta: 'Subscribe',
        checkoutPlan: 'basique',
      },
      {
        key: 'pro',
        name: 'Pro',
        tag: '€290/month',
        tagAnnual: '€2784/year',
        description: "To orchestrate multiple apps and unlock OpenMeta's full AI arsenal.",
        features: [
          'Up to 10 applications',
          'All AI blocks from the 5 verticals',
          'Unlimited concept generations',
          'Custom domain',
          'Priority support (< 24h)',
          'Detailed usage analytics',
        ],
        cta: 'Upgrade to Pro',
        checkoutPlan: 'pro',
      },
      {
        key: 'entreprise',
        name: 'Enterprise / Agency',
        tag: 'On request',
        tagAnnual: 'On request',
        description: 'For agencies and organizations rolling out OpenMeta at scale.',
        features: [
          'Unlimited applications',
          'Dedicated human onboarding',
          'SSO & role management',
          'Dedicated deployment (EU cloud)',
          '99.9% SLA',
          'GDPR & security audit',
        ],
        cta: 'Contact us',
      },
    ],
    faq: [
      {
        q: 'Can I change plans anytime?',
        a: 'Yes — you can move from Basic to Pro (or the other way) whenever you like. Proration is handled automatically by our billing system.',
      },
      {
        q: 'Can I cancel anytime?',
        a: 'Yes. No commitment — cancel in one click from your account; you keep access until the end of the paid period.',
      },
      {
        q: 'What is the difference between Basic and Pro?',
        a: 'Basic lets you publish 1 application with the essential blocks. Pro unlocks up to 10 applications, every AI block from the 5 verticals and priority support.',
      },
      {
        q: 'Can I try before I pay?',
        a: 'Absolutely: the concept generator is freely available at openmeta.tech/generateur. You see exactly what the AI produces before subscribing.',
      },
      {
        q: 'Where is my data hosted?',
        a: 'In France, with a certified provider. We never resell your data and we strictly follow GDPR.',
      },
      {
        q: 'Do you offer discounts for nonprofits and education?',
        a: 'Yes — reach out. We have a dedicated offer for nonprofits, schools and research projects.',
      },
    ],
  },
  contact: {
    eyebrow: 'Contact',
    title_l1: 'Let’s talk about',
    title_l2: 'your project.',
    subtitle:
      'Demo, partnership, press or a simple question — we reply within 48 business hours.',
    email_label_short: 'Direct email',
    name_label: 'Name',
    name_placeholder: 'Your name',
    email_label: 'Email',
    subject_label: 'Subject',
    subject_placeholder: 'Choose a subject',
    subjects: {
      demo: 'Demo',
      partenariat: 'Partnership',
      presse: 'Press',
      autre: 'Other',
    },
    message_label: 'Message',
    message_placeholder: "Tell us about your project, your needs, your timing…",
    message_hint: 'Between 10 and 2000 characters.',
    submit: 'Send the message',
    submitting: 'Sending…',
    success_title: 'Message sent.',
    success_subtitle: "Thanks! We'll get back to you very soon.",
    success_toast: "Thanks! Your message is on its way — we'll get back to you very soon.",
    error_generic_toast: 'Please fix the fields highlighted above.',
    validation: {
      name_required: 'Please enter your name.',
      name_too_long: 'This name is too long.',
      email_required: 'Please enter your email.',
      email_invalid: "This email address doesn't look valid.",
      subject_required: 'Please choose a subject.',
      message_min: 'Your message must contain at least 10 characters.',
      message_max: 'Your message must be under 2000 characters.',
    },
  },
  generator: {
    eyebrow: 'The Generator',
    title_l1: 'Describe your idea.',
    title_l2: 'AI imagines the app.',
    subtitle:
      'In seconds, get a complete application concept — AI modules, key screens and no-code blocks ready to assemble.',
    seo_description:
      "Describe your app idea in English. OpenMeta's AI imagines your no-code concept — name, modules, key screens, blocks — in seconds.",
    label_universe: 'Choose your vertical',
    label_description: 'Describe your app idea',
    desc_hint: 'Between {min} and {max} characters. Talk about your work, not the tech.',
    avg_time: 'Average time: 10 seconds • fully in English',
    submit: 'Generate my concept',
    placeholders: {
      sante: 'e.g. An app that helps physiotherapists follow up their patients at home with reminders and short exercise videos.',
      culture: 'e.g. An interactive guide for my art gallery that tells the story of every piece on display.',
      formation: 'e.g. A short video training for shopkeepers who want to launch their own online store.',
      langues: 'e.g. A Spanish learning companion to prepare for a 3-week trip to Seville.',
      social: 'e.g. A content studio that generates my captions and hashtags for Instagram and TikTok every week.',
    },
    loading_steps: [
      'Analyzing your idea…',
      'Picking the right no-code blocks…',
      'Assembling AI modules…',
      'Writing the concept…',
    ],
    loading_title: 'Your concept is taking shape.',
    loading_avg: 'It usually takes about 10 seconds.',
    gate_title: 'Unlock your full concept',
    gate_subtitle: 'AI modules, key screens, no-code blocks and target users — just one email.',
    gate_submit: 'Unlock the concept',
    gate_submitting: 'Unlocking…',
    gate_no_spam: "No spam — just a heads-up when OpenMeta opens.",
    unlocked_badge: 'Concept unlocked',
    section_users: 'Who is it for?',
    section_modules: 'Modules ({count})',
    section_screens: 'Key screens ({count})',
    section_blocks: 'No-code blocks to assemble',
    estimate_prefix: 'OpenMeta estimate:',
    estimate_suffix: 'to build this app.',
    regenerate: 'Generate another concept',
    samples_eyebrow: 'Created right here',
    samples_title: 'A few concepts fresh out of the generator.',
    samples_cta: 'See our applications in production',
    unlock_success: 'Concept unlocked and waitlist joined!',
    unlock_already_in: "Welcome back! Concept unlocked.",
    validation: {
      desc_min: 'Describe your idea in at least {min} characters.',
      desc_max: 'Keep it under {max} characters.',
      universe_required: 'Pick a vertical.',
    },
  },
  not_found: {
    badge: 'Error 404',
    title: '404',
    heading: "This page doesn't exist yet.",
    subtitle:
      "The OpenMeta universe is modular — but this module hasn't been assembled yet. Head back home, or let the generator guide you.",
    cta_home: 'Back to home',
    cta_generator: 'Try the generator',
    seo_title: 'Page not found (404)',
    seo_description: "This page doesn't exist yet. Head back to the OpenMeta home.",
  },
  placeholder: {
    eyebrow: 'Coming soon',
    back_home: 'Back to home',
    no_spam: 'Notified first — no spam.',
    pages: {
      mentions: {
        title: 'Legal notice',
        subtitle:
          'Publisher, hosting and legal information will be published at OpenMeta launch.',
      },
      privacy: {
        title: 'Privacy',
        subtitle:
          "Our privacy policy will be published at launch. In the meantime, your waitlist data is only used to notify you.",
      },
    },
  },
  medlib_qr: {
    banner_text: 'your health at your fingertips',
    dismiss_aria: 'Dismiss MedLib announcement',
  },

  seo: {
    home_title: 'OpenMeta — No Code. No Limits. Just AI.',
    home_description:
      "OpenMeta is the No-Code + AI platform that gives domain experts the power to build: health, culture, learning, languages and social media. Describe your idea, the AI assembles your blocks.",
    applications_title: 'Our Applications',
    applications_description:
      'Real applications shipping today: ARIA Radiology, MedUrgence, Atlantys, MedSynapse, Lexia School. Built by and for domain experts with OpenMeta.',
    pricing_title: 'Pricing',
    pricing_description:
      'OpenMeta pricing tiers: Basic at €29/month, Pro at €290/month for 10 apps, Enterprise/Agency on request. Monthly/annual toggle with -20% and a full FAQ.',
    contact_title: 'Contact',
    contact_description:
      "Let's talk about your project — demo, partnership, press or a simple question. We reply within 48 business hours.",
    generator_title: 'The Generator',
    prototype_title: 'Prototype — OpenMeta',
    prototype_description: "Discover the interactive mockup of an application imagined by OpenMeta's AI, screen by screen, in a real phone frame.",
    register_title: 'Create your account — OpenMeta',
    register_description: 'Create your OpenMeta creator account to start building no-code apps.',
    login_title: 'Sign in — OpenMeta',
    login_description: 'Access your OpenMeta creator space.',
    studio_title: 'Studio — OpenMeta',
    studio_description: 'Your creator space at OpenMeta: active plan, quota, and your future applications.',
  },
  auth: {
    register_eyebrow: 'Join OpenMeta',
    register_title: 'Create your creator account.',
    register_subtitle: 'One account to manage your concepts, prototypes and soon your no-code applications.',
    register_cta: 'Create my account',
    login_eyebrow: 'Welcome back',
    login_title: 'Sign in.',
    login_subtitle: 'Access your studio and applications.',
    login_cta: 'Sign in',
    email_label: 'Email',
    password_label: 'Password',
    password_placeholder: 'Pick a password',
    password_hint: '8 characters minimum.',
    submitting: 'One moment…',
    have_account: 'Already have an account?',
    no_account: 'No account yet?',
    register_success: 'Welcome! Your account is ready.',
    login_success: 'Glad to see you again.',
    logout: 'Sign out',
    my_apps: 'My applications',
    account_menu_title: 'My account',
    errors: {
      password_required: 'Pick a password.',
      password_too_short: '8 characters minimum.',
      fill_both: 'Please fill in email and password.',
      terms_required: 'You must accept the Terms of Use and the Privacy Policy to continue.',
    },
    terms_prefix: 'I agree to the',
    terms_and: 'and',
    terms_label_terms: 'Terms of Use',
    terms_label_privacy: 'Privacy Policy',
  },
  legal: {
    eyebrow: 'Legal documents',
    last_updated: 'Last updated',
    toc_label: 'Table of contents',
    contact_title: 'Contact',
    courtesy_note: "This is a courtesy translation. The French version prevails in case of any discrepancy.",
  },
  studio: {
    eyebrow: 'Studio',
    title: 'Hi, {email}',
    subtitle: 'Your creator space. Build apps, publish them to openmeta.tech/app/…, manage submissions.',
    plan_label: 'Your plan',
    plan_none: 'None',
    plan_basique: 'Basic',
    plan_pro: 'Pro',
    plan_illimite: 'Founder Access',
    unlimited_apps: 'Unlimited apps',
    unlimited_hint: 'Publish as many apps as you like — no quota, no ceiling.',
    interval_month: 'Monthly',
    interval_year: 'Annual',
    quota_label: 'Published apps',
    apps_singular: 'application',
    apps_plural: 'applications',
    my_apps: 'My applications',
    create_app: 'Create an app',
    empty_title: 'No apps yet.',
    empty_subtitle: 'Build your first mini-app in 5 minutes, or start from an AI-generated concept.',
    explore_generator: 'Open the generator',
    noplan_title: 'Pick a plan to get started.',
    noplan_subtitle: "You don't have an active plan yet. Pick Basic or Pro to build and publish your apps.",
    noplan_cta: 'See pricing',
    needs_plan: 'Pick a plan to create your first app.',
    status_published: 'Published',
    status_draft: 'Draft',
    edit: 'Edit',
    publish: 'Publish',
    unpublish: 'Unpublish',
    delete: 'Delete',
    open_public: 'Open public page',
    published_toast: 'App published! It is live.',
    unpublished_toast: 'App unpublished.',
    deleted_toast: 'App deleted.',
    delete_failed: 'Delete failed.',
    delete_confirm_title: 'Delete this app?',
    delete_confirm_body: 'The public page and all submissions will be removed permanently.',
    blocks: 'blocks',
    submissions: 'submissions',
    chat_messages: 'messages',
    activity_7d: 'Activity 7 days',
    visits: 'visits',
    moderation_title: 'Moderation',
    moderation_no_reason: 'This app has been paused. Contact support to unlock.',
    moderation_blocked: 'Publishing blocked by moderation',
  },
  builder: {
    title: 'Builder',
    description: 'Edit your app, preview it, then publish.',
    back_studio: 'Back to studio',
    settings: 'Settings',
    name_label: 'App name',
    slug_label: 'Slug (public URL)',
    vertical_label: 'Vertical',
    lang_label: 'Language',
    description_label: 'Description (SEO)',
    tabs: {
      build: 'Builder',
      preview: 'Preview',
      submissions: 'Submissions',
    },
    blocks: {
      content: 'Content page',
      chatbot: 'AI Chatbot',
      form: 'Form',
    },
    add_block: 'Add a block:',
    blocks_empty: 'No blocks yet. Add a content page, a chatbot or a form to get started.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Draft saved.',
    save_before_publish: 'Save before publishing.',
    published: 'App published! 🚀',
    unpublished: 'App unpublished.',
    preview_note: 'Public preview. Interactive blocks (chatbot, form) are disabled here — publish to test them live.',
    concept_panel_title: 'Your concept blueprint',
    concept_modules: 'Modules',
    concept_screens: 'Key screens',
    tips_title: 'Best practices',
    tips: {
      slug: '• The slug is your public URL and cannot contain spaces.',
      persona: "• The chatbot persona directly shapes its tone and guardrails.",
      publish: '• Basic = 1 published app, Pro = 10. Drafts are unlimited.',
    },
    total_submissions: 'submissions in total',
    refresh: 'Refresh',
    export_csv: 'Export CSV',
    export_ok: 'CSV downloaded.',
    export_failed: "Can't export right now.",
    no_submissions_yet: 'No submissions yet.',
    errors: {
      create_failed: 'Could not create the application.',
      save_failed: "Can't save right now.",
      publish_failed: "Can't publish right now.",
      not_found: "This application doesn't exist anymore.",
      invalid: 'Some information looks invalid.',
      quota: 'Publish quota reached. Upgrade your plan.',
      max_blocks: 'Maximum 8 blocks per application.',
      slug_too_short: 'Slug must be at least 3 characters.',
      slug_too_long: 'Slug is too long (40 characters max).',
      slug_format: 'Slug must be kebab-case (a-z, 0-9, hyphens).',
      slug_reserved: 'This slug is reserved. Pick another one.',
      slug_taken: 'This slug is already taken. Pick another one.',
    },
    open_in_builder: 'Open in the builder',
    open_in_builder_needs_login: 'Sign in to open in the builder',
    open_in_builder_needs_plan: 'Upgrade to Basic or Pro to open in the builder',
    opening: 'Opening…',
  },
  prototype: {
    build_cta: 'Build the prototype',
    build_cta_loading: 'Building…',
    build_cta_view: 'View the prototype',
    loading_eyebrow: 'Prototype in progress',
    loading_title: "We're assembling your app.",
    loading_steps: [
      'Analyzing the concept…',
      'Assembling screens…',
      'Applying your theme…',
      'Injecting sample data…',
      'Polishing the details…',
    ],
    teaser_eyebrow: 'Coming soon',
    teaser_title: 'Publish this app for real with OpenMeta.',
    teaser_subtitle:
      "The no-code builder is coming: take this prototype, personalize it and ship it live. Pick your plan now to be ready when we open.",
    teaser_cta: 'See pricing',
    disclaimer:
      "This prototype is a visual mockup: sample content is generated for illustration, no real data processing takes place.",
    locked_title: 'Concept still locked.',
    locked_subtitle: 'Unlock this concept from the generator to build its prototype.',
    locked_cta: 'Open the generator',
    notfound_title: 'Prototype not found.',
    notfound_subtitle: "This concept doesn't exist anymore. Generate a new one from the generator.",
    notfound_cta: 'Open the generator',
    error_title: 'Small hiccup.',
    error_subtitle: 'Building the prototype failed. Please try again shortly.',
  },
  checkout_success: {
    seo_title: 'Payment — OpenMeta',
    seo_description: 'Confirmation of your OpenMeta subscription and next steps.',
    polling_eyebrow: 'Confirmation',
    polling_title: 'Confirming your payment…',
    polling_subtitle: "Just a few seconds — we're pulling the confirmation from Stripe.",
    success_eyebrow: 'Payment confirmed',
    success_title: 'Welcome — your subscription is active.',
    success_subtitle: "You'll receive a confirmation email with your invoice shortly. You can already explore the concept generator and start planning your first application.",
    plan_label: 'Plan',
    interval_month: 'Monthly',
    interval_year: 'Annual',
    confirmation_sent_to: 'Receipt sent to',
    cta_home: 'Back to home',
    cta_generator: 'Open the generator',
    pending_eyebrow: 'Pending',
    pending_title: 'Your payment is still being processed.',
    pending_subtitle: 'No charge until the payment is confirmed. You can close this page and come back later, or restart from the pricing page.',
    cta_back_pricing: 'Back to pricing',
    notfound_title: 'Session not found',
    notfound_subtitle: "This checkout session is no longer valid or never existed. Please restart from the pricing page.",
    error_title: 'Small hiccup',
    error_subtitle: "We're having trouble confirming your payment in real time. Please check your inbox in a few minutes — the Stripe invoice is the source of truth.",
  },
};
